import CloseIcon from '@mui/icons-material/Close'
import { Dialog } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AppUppdatePopupContext } from 'src/components/AppUpdate/AppUpdatePopupContext'
import useAppUpdateAction from 'src/components/AppUpdate/hooks/useAppupdateAction'
import {
  APP_CONSTANTS,
  APP_UPDATE_ACTIONS,
  DISPATCH_EVENT_KEYS,
} from 'src/consumer/constants'
import { normalizeApiBaseUrl, resolveApiDomainConfig } from 'src/consumer/services/apiDomainConfig'
import { putDataWithParams } from 'src/consumer/services/featureServices'
import { isViteAppRunningInProduction } from 'src/helper'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  clearBrowserCaches,
  clearReloadAttempts,
  getMaxReloadAttempts,
  incrementReloadAttempts,
} from 'src/components/AppUpdate/appRefreshUtils'
export default function AppUpdatePopup({ children }) {
  const [isSuggestedUpdateToastOpen, setIsSuggestedUpdateToastOpen] = useState(false)
  const [isForceUpdateDialogOpen, setIsForceUpdateDialogOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showManualInstructions, setShowManualInstructions] = useState(false)
  const SUGGEST_UPDATE = APP_UPDATE_ACTIONS.SUGGEST_UPDATE
  const FORCE_UPDATE = APP_UPDATE_ACTIONS.FORCE_UPDATE

  const { prepareAppUpdateActionPayload } = useAppUpdateAction()
  const intervalRef = useRef(null)
  const isFetchingRef = useRef(false)

  // Clean up cache-busting query param after a successful refresh
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has('_appRefresh')) {
        url.searchParams.delete('_appRefresh')
        window.history.replaceState(null, '', url.toString())
      }
    } catch {
      /* ignore */
    }
  }, [])

  const handleUpdateNow = useCallback(async () => {
    setIsUpdating(true)
    setShowManualInstructions(false)

    const attempts = incrementReloadAttempts()

    if (attempts > getMaxReloadAttempts()) {
      setIsUpdating(false)
      setShowManualInstructions(true)
      return
    }

    try {
      await clearBrowserCaches()
    } catch {
      /* ignore */
    }

    // Navigate with a cache-busting query parameter to bypass cached index.html
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('_appRefresh', Date.now().toString())
      window.location.replace(url.toString())
    } catch {
      window.location.reload()
    }

    // Safety fallback: if navigation didn't trigger within 5s, force a plain reload
    setTimeout(() => {
      setIsUpdating(false)
      window.location.reload()
    }, 5000)
  }, [])

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) return
    isFetchingRef.current = true

    try {
      const payload = prepareAppUpdateActionPayload()
      const response = await putDataWithParams(`/api/nc/app-update-action`, payload, {
        packageId: 'com.aumhum.web',
        platform: 'web',
        destination: isViteAppRunningInProduction()
          ? APP_CONSTANTS.LIVE_APP_URL
          : APP_CONSTANTS.BETA_APP_URL,
      })
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.action
      ) {
        const action = response?.data?.data?.action
        const defaultRestApiServer = response?.data?.data?.defaultRestApiServer

        if (action === SUGGEST_UPDATE || action === FORCE_UPDATE) {
          const updateEvent = new CustomEvent(DISPATCH_EVENT_KEYS.appUpdateActionEvent, {
            detail: { action: action },
          })
          window.dispatchEvent(updateEvent)
        }

        if (action === SUGGEST_UPDATE) {
          setIsSuggestedUpdateToastOpen(true)
          setIsForceUpdateDialogOpen(false)
        } else if (action === FORCE_UPDATE) {
          setIsSuggestedUpdateToastOpen(false)
          setIsForceUpdateDialogOpen(true)
        } else {
          setIsSuggestedUpdateToastOpen(false)
          setIsForceUpdateDialogOpen(false)
          clearReloadAttempts()
        }
        const normalizedRecommendedApiServer = normalizeApiBaseUrl(defaultRestApiServer)
        if (defaultRestApiServer && !normalizedRecommendedApiServer) {
          console.error('Invalid defaultRestApiServer URL received:', defaultRestApiServer)
        }
        if (normalizedRecommendedApiServer) {
          const apiDomainConfig = resolveApiDomainConfig()
          const matchesKnownDefault =
            normalizedRecommendedApiServer === apiDomainConfig.defaultApiBaseUrl ||
            normalizedRecommendedApiServer === apiDomainConfig.storedDefaultApiBaseUrl

          if (!matchesKnownDefault) {
            console.info(
              '[App update] Recommended default API server received. No automatic change was applied.',
              {
                recommendedApiServer: normalizedRecommendedApiServer,
                activeApiServer: apiDomainConfig.effectiveApiBaseUrl,
                builtInDefaultApiServer: apiDomainConfig.defaultApiBaseUrl,
              },
            )
          }
        }
      } else {
        clearReloadAttempts()
      }
    } catch (error) {
      console.error('App update check failed:', error)
    } finally {
      isFetchingRef.current = false
    }
  }, [FORCE_UPDATE, SUGGEST_UPDATE, prepareAppUpdateActionPayload])

  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(fetchData, 600000) //10 mins
  }, [fetchData])

  useEffect(() => {
    // Delay first check so critical page APIs finish before this fires
    const delayId = setTimeout(() => {
      fetchData()
      startInterval()
    }, 5000)

    const handleScreenRegistered = () => {
      console.log('Restarting app update interval...')
      startInterval()
    }

    window.addEventListener('refreshAppUpdateInterval', handleScreenRegistered)

    return () => {
      clearTimeout(delayId)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      window.removeEventListener('refreshAppUpdateInterval', handleScreenRegistered)
    }
  }, [fetchData, startInterval])

  function renderManualInstructions() {
    return (
      <Dialog
        open
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
        }}
        PaperProps={{
          sx: { padding: '32px 8px', margin: '10px', maxWidth: 'none', borderRadius: '10px' },
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: '16px',
            maxWidth: '340px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '24px' }}>
            <b>Update Required</b>
          </div>
          <div style={{ marginTop: '12px' }}>
            We couldn&apos;t update automatically. Please clear your browser cache and reload:
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#555', textAlign: 'left' }}>
            <div>
              <b>Chrome/Edge:</b> Ctrl+Shift+Delete &rarr; Clear cache &rarr; Reload
            </div>
            <div>
              <b>Safari:</b> Settings &rarr; Clear History and Website Data
            </div>
            <div>
              <b>Mobile:</b> Clear browser cache in settings
            </div>
          </div>
          <button
            type="button"
            style={{
              color: '#fff',
              background: '#000',
              borderRadius: '4px',
              marginTop: '24px',
              padding: '8px 24px',
              cursor: 'pointer',
              border: 'none',
            }}
            onClick={() => {
              clearReloadAttempts()
              handleUpdateNow()
            }}
          >
            Try Again
          </button>
        </div>
      </Dialog>
    )
  }

  function renderPopup() {
    if (showManualInstructions) {
      return renderManualInstructions()
    }

    if (isSuggestedUpdateToastOpen && popupAllowedOnThisUrl()) {
      return (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '200px',
            height: 'auto',
            userSelect: 'none',
            background: '#fff',
            padding: '8px',
            borderRadius: '8px',
            color: '#000',
            zIndex: 2147482999,
            fontSize: '12px',
            border: '1px solid #cecece',
          }}
        >
          <div className="d-flex" style={{ justifyContent: 'space-between', gap: '8px' }}>
            <div>
              <div>A new app version is available.</div>
              <button
                type="button"
                style={{
                  color: '#fff',
                  background: '#000',
                  borderRadius: '4px',
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  border: 'none',
                  padding: '4px 12px',
                  marginTop: '4px',
                  opacity: isUpdating ? 0.7 : 1,
                }}
                disabled={isUpdating}
                onClick={handleUpdateNow}
              >
                {isUpdating ? 'Updating...' : 'Update Now'}
              </button>
            </div>
            <CloseIcon
              style={{ fontSize: '16px', cursor: 'pointer' }}
              onClick={() => setIsSuggestedUpdateToastOpen(false)}
            />
          </div>
        </div>
      )
    }

    if (isForceUpdateDialogOpen && popupAllowedOnThisUrl()) {
      return (
        <Dialog
          open={isForceUpdateDialogOpen}
          disableEscapeKeyDown
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return
            }
          }}
          PaperProps={{
            sx: { padding: '32px 8px', margin: '10px', maxWidth: 'none', borderRadius: '10px' },
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              padding: '16px',
              maxWidth: '300px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '24px' }}>
              <b>New Update Released</b>
            </div>
            <div>You need to update the app to continue</div>
            <button
              type="button"
              style={{
                color: '#fff',
                background: '#000',
                borderRadius: '4px',
                marginTop: '48px',
                padding: '8px 24px',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                border: 'none',
                opacity: isUpdating ? 0.7 : 1,
              }}
              disabled={isUpdating}
              onClick={handleUpdateNow}
            >
              {isUpdating ? 'Updating...' : 'Update Now'}
            </button>
          </div>
        </Dialog>
      )
    }
  }

  return (
    <AppUppdatePopupContext.Provider
      value={{
        reSendAppUpdateAction: fetchData,
        requestAppRefresh: handleUpdateNow,
        isAppRefreshInProgress: isUpdating,
      }}
    >
      {renderPopup()}
      {children}
    </AppUppdatePopupContext.Provider>
  )
}

function popupAllowedOnThisUrl() {
  const blockedPaths = [
    '/legal',
    '/terms',
    '/privacy-policy',
    '/privacy-policy-1',
    '/cookie-policy',
    '/instructor-terms',
    '/copy-of-privacy-policy',
  ]

  const currentPath = window.location.pathname

  return !blockedPaths.includes(currentPath) && !currentPath.includes('/webview/')
}
